import { Col, Image, Row, Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useChatContext } from "../../context/ChatContext";
import ShowContent from "../ShowContent/ShowContent";
import "./style.scss";
import { useImageLoader } from "../customHook/ImageLoader";
import noImage from "../../../src/assets/no-image.avif";
import MediaViewerModal from "../modal/MediaViewerModal";
import { useMediaResource } from "../../hooks/useMediaResource";
import apiFactory from "../../api";

const ImagePiece = ({
  path,
  index,
  xsCol,
  mdCol,
  cssType,
  openModal,
  bg,
  overlay,
  content,
  isMention,
  styleImg,
  imagesToShow,
  styleLoadingDiv,
  imageData,
}) => {
  const { messageRef, wasAtBottomMessageRef } = useChatContext();

  const { imageUrl, loading, error } = useImageLoader(
    !imageData?.isUploading && !path ? imageData?.resourceId : null,
  );
  const source = path || imageUrl;

  const scrollAfterImageLoad = useCallback(() => {
    const messageBox = messageRef.current;

    if (!messageBox) return;

    if (wasAtBottomMessageRef.current) {
    }
  }, [messageRef, wasAtBottomMessageRef]);

  useEffect(() => {
    scrollAfterImageLoad();
  }, [imageUrl, loading, scrollAfterImageLoad]);

  const renderImage = useMemo(() => {
    if (imageData?.isUploading && imageData?.pathUrl) {
      return (
        <img
          src={imageData.pathUrl}
          style={styleImg}
          alt="uploading"
          className="w-full h-full object-cover rounded-md"
        />
      );
    }
    if (loading && !source && !imageData?.isUploading) {
      return (
        <div
          style={styleLoadingDiv?.() || {}}
          className="flex items-center justify-center min-h-[100px]"
        >
          <Spin />
        </div>
      );
    }

    if (error && !source && !imageData?.isUploading) {
      return (
        <Image
          style={styleImg}
          className="bg-white"
          preview={false}
          src={noImage}
          alt="No Image"
        />
      );
    }

    if (source) {
      return <Image src={source} style={styleImg} preview={false} />;
    }

    return null;
  }, [source, loading, error, styleImg, styleLoadingDiv, imageData]);

  return (
    <Col
      xs={xsCol}
      md={mdCol}
      className={`border background ${cssType} flex ${
        source ? "flex-col" : "flex-row"
      } justify-center`}
    >
      <div className="flex flex-col mb-[2px] pointer max-w-[30vw] items-center justify-center">
        <div className="cursor-pointer" onClick={() => openModal(imageData)}>
          {renderImage}
        </div>

        {content?.length > 0 && (
          <ShowContent
            content={content}
            isMention={isMention}
            maxLength={300}
            hasImage={true}
          />
        )}
        {overlay}
      </div>
    </Col>
  );
};

const GridPhotos = ({
  images,
  countFrom,
  hideOverlay = true,
  bg,
  isSelf,
  content,
  isMention,
  createdAt,
  senderId,
}) => {
  const mediaResource = useMediaResource();

  const [height, setHeight] = useState("!h-[380px]");
  const [isImageLoaded, setIsImageLoaded] = useState(true);

  const openModal = async (imageData) => {
    const result = await apiFactory.conversationApi.detailResource({
      mainPath: imageData?.mainPath,
    });

    mediaResource?.setSelectedMedia(result?.data);
    mediaResource?.setMediaTypes(["IMAGE"]);
    mediaResource?.setIsFetchData(true);
    mediaResource?.setVisible(true);
  };

  const imagesToShow = useMemo(() => {
    if (countFrom && images?.length > countFrom) {
      return countFrom;
    } else {
      return images?.length;
    }
  }, [countFrom, images]);

  const loadedImage = () => {
    setHeight("!max-h-[380px]");
  };

  const handleImageError = () => {
    setIsImageLoaded(false);
  };

  const renderTwo = () => {
    const conditionalRender =
      [3, 4].includes(images?.length) ||
      (images?.length > +countFrom && [3, 4].includes(+countFrom));

    if (!isImageLoaded) {
      return <div>Loading...</div>;
    }

    return (
      <Row>
        <ImagePiece
          path={
            conditionalRender
              ? images?.[1]?.thumbnailPreviewPath || images?.[1]?.mainPath
              : images?.[0]?.thumbnailPreviewPath || images?.[0]?.mainPath
          }
          index={conditionalRender ? 1 : 0}
          xsCol={12}
          mdCol={12}
          cssType={"height-two"}
          minHeightLoading={"min-h-[200px]"}
          openModal={openModal}
          height={height}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          renderOverlay={renderOverlay}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[1]?.width
                : images?.[0]?.width,
              imgHeight: conditionalRender
                ? images?.[1]?.height
                : images?.[0]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[1] : images?.[0]}
        />
        <ImagePiece
          path={
            conditionalRender
              ? images?.[2]?.thumbnailPreviewPath || images?.[2]?.mainPath
              : images?.[1]?.thumbnailPreviewPath || images?.[1]?.mainPath
          }
          index={conditionalRender ? 2 : 1}
          xsCol={12}
          mdCol={12}
          cssType={"height-two"}
          minHeightLoading={"min-h-[200px]"}
          openModal={openModal}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          height={height}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          renderOverlay={renderOverlay}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[2]?.width
                : images?.[1]?.width,
              imgHeight: conditionalRender
                ? images?.[2]?.height
                : images?.[1]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[2] : images?.[1]}
        />
      </Row>
    );
  };

  const renderThree = () => {
    const conditionalRender =
      images?.length === 4 || (images?.length > +countFrom && +countFrom === 4);

    const overlay =
      !countFrom ||
      countFrom > 5 ||
      (images?.length > countFrom && [4, 5].includes(+countFrom))
        ? renderCountOverlay(true)
        : renderOverlay(conditionalRender ? 3 : 4);

    if (!isImageLoaded) {
      return <div>Loading...</div>;
    }

    return (
      <Row>
        <ImagePiece
          path={
            conditionalRender
              ? images?.[1]?.thumbnailPreviewPath || images?.[1]?.mainPath
              : images?.[2]?.thumbnailPreviewPath || images?.[2]?.mainPath
          }
          index={conditionalRender ? 1 : 2}
          xsCol={12}
          mdCol={8}
          cssType={"height-three"}
          minHeightLoading={"min-h-[150px]"}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          openModal={openModal}
          height={height}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[1]?.width
                : images?.[2]?.width,
              imgHeight: conditionalRender
                ? images?.[1]?.height
                : images?.[2]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[1] : images?.[2]}
        />
        <ImagePiece
          path={
            conditionalRender
              ? images?.[2]?.thumbnailPreviewPath || images?.[2]?.mainPath
              : images?.[3]?.thumbnailPreviewPath || images?.[3]?.mainPath
          }
          index={conditionalRender ? 1 : 0}
          xsCol={12}
          mdCol={8}
          cssType={"height-three"}
          minHeightLoading={"min-h-[150px]"}
          openModal={openModal}
          height={height}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[2]?.width
                : images?.[3]?.width,
              imgHeight: conditionalRender
                ? images?.[2]?.height
                : images?.[3]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[2] : images?.[3]}
        />
        <ImagePiece
          path={
            conditionalRender
              ? images?.[3]?.thumbnailPreviewPath || images?.[3]?.mainPath
              : images?.[4]?.thumbnailPreviewPath || images?.[4]?.mainPath
          }
          index={conditionalRender ? 1 : 0}
          xsCol={12}
          mdCol={8}
          cssType={"height-three"}
          minHeightLoading={"min-h-[150px]"}
          openModal={openModal}
          height={height}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          overlay={overlay}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[3]?.width
                : images?.[4]?.width,
              imgHeight: conditionalRender
                ? images?.[3]?.height
                : images?.[4]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[3] : images?.[4]}
        />
      </Row>
    );
  };

  const renderOverlay = (id) => {
    if (hideOverlay) {
      return false;
    }

    return [<div key={`cover-${id}`} className="cover slide"></div>];
  };

  const renderCountOverlay = (more) => {
    const extra = images.length - (countFrom && countFrom > 5 ? 5 : countFrom);

    return [
      more && <div key="count" className="cover"></div>,
      more && (
        <div
          onClick={() => openModal(images?.[4])}
          key="count-sub"
          className="cover-text w-full cursor-pointer text-[200%]"
        >
          +{extra}
        </div>
      ),
    ];
  };

  const calculateCssLoadingDiv = ({
    type = "width",
    imgWidth,
    imgHeight,
    value,
    minHeightVw = 5,
  } = {}) => {
    if (!imgWidth || !imgHeight) return null;

    const vwUnit = window.innerWidth / 100;
    const aspectRatio = imgWidth / imgHeight;
    const maxPx = value * vwUnit;
    const minHeightPx = minHeightVw * vwUnit;

    let widthPx, heightPx;

    if (type === "width") {
      if (imgWidth <= maxPx) {
        widthPx = imgWidth;
        heightPx = imgHeight;
      } else {
        widthPx = maxPx;
        heightPx = maxPx / aspectRatio;
      }
    } else {
      if (imgHeight <= maxPx) {
        heightPx = imgHeight;
        widthPx = imgWidth;
      } else {
        heightPx = maxPx;
        widthPx = maxPx * aspectRatio;
      }
    }

    if (heightPx < minHeightPx) {
      heightPx = minHeightPx;
      widthPx = heightPx * aspectRatio;
    }

    return {
      minWidth: `${widthPx / vwUnit}vw`,
      minHeight: `${heightPx / vwUnit}vw`,
    };
  };

  return (
    <div>
      {imagesToShow === 1 && (
        <Row>
          <ImagePiece
            path={images?.[0]?.thumbnailPreviewPath || images?.[0]?.mainPath}
            index={0}
            xsCol={24}
            mdCol={24}
            cssType={"height-two"}
            minHeightLoading={"min-h-[200px]"}
            openModal={openModal}
            height={height}
            loadedImage={loadedImage}
            handleImageError={handleImageError}
            bg={bg}
            isMention={isMention}
            content={content}
            styleImg={{
              minHeight: "5vw",
              maxHeight: "20vw",
              maxWidth: "20vw",
              objectFit: "cover",
              transform: "translateZ(0.1px)",
            }}
            imagesToShow={imagesToShow}
            styleLoadingDiv={() =>
              calculateCssLoadingDiv({
                type: "width",
                imgWidth: images?.[0]?.width,
                imgHeight: images?.[0]?.height,
                value: 20,
              })
            }
            imageData={images?.[0]}
          />
        </Row>
      )}
      {[3, 4].includes(imagesToShow) && (
        <Row>
          <ImagePiece
            path={images?.[0]?.thumbnailPreviewPath || images?.[0]?.mainPath}
            index={0}
            xsCol={24}
            mdCol={24}
            cssType={"height-two"}
            minHeightLoading={"min-h-[200px]"}
            openModal={openModal}
            height={height}
            styleImg={{
              minHeight: "5vw",
              maxHeight: "380px",
              objectFit: "cover",
              transform: "translateZ(0.1px)",
              objectPosition: "center",
            }}
            loadedImage={loadedImage}
            handleImageError={handleImageError}
            bg={bg}
            styleLoadingDiv={() =>
              calculateCssLoadingDiv({
                type: "height",
                imgWidth: images?.[0]?.width,
                imgHeight: images?.[0]?.height,
                value: 20,
              })
            }
            imageData={images?.[0]}
          />
        </Row>
      )}
      {imagesToShow >= 2 && imagesToShow !== 4 && renderTwo()}
      {imagesToShow >= 4 && renderThree()}

      {/* Modal viewer */}
      {mediaResource?.visible && (
        <MediaViewerModal mediaResource={mediaResource} />
      )}
    </div>
  );
};

export default GridPhotos;
